import { filterFns } from '@tanstack/react-table';

import {
  filterByNewData,
  filterBySelectedCode,
  filterBySelectedCurve,
  filterByStatus
} from './lib/filters';
import { sortAsFrequency, sortAsNumber, sortAsPeriodType } from './lib/sorts';

export const ADDITIONAL_ITEM = 'AdditionalItem';
export const NEW_DATA_URL_PARAM = 'newData';
export type CountedLabelFunction = (
  key: string,
  joined?: boolean
) => {
  label: string;
  count: number;
}[];

export const FILTER_KEYS = {
  COMMODITY: 'Commodity',
  TRADING_HUB: 'TradingHub',
  DELIVERY_BASIS: 'DeliveryBasis',
  PRICING_BASIS: 'PricingBasis',
  UNITS: 'Units',
  CURRENCY: 'Currency',
  FREQUENCY: 'Frequency',
  INDEX_MODULE: 'IndexModule'
} as const;

export type FilterKey = (typeof FILTER_KEYS)[keyof typeof FILTER_KEYS];

const countedLabelColumn =
  (key: FilterKey, joined = false) =>
  (getCountedLabels: CountedLabelFunction) =>
    getCountedLabels(key, joined);

export const labeledFilterColumns: Record<
  FilterKey,
  (getCountedLabels: CountedLabelFunction) => ReturnType<CountedLabelFunction>
> = {
  [FILTER_KEYS.COMMODITY]: countedLabelColumn(FILTER_KEYS.COMMODITY),
  [FILTER_KEYS.TRADING_HUB]: countedLabelColumn(FILTER_KEYS.TRADING_HUB),
  [FILTER_KEYS.DELIVERY_BASIS]: countedLabelColumn(FILTER_KEYS.DELIVERY_BASIS),
  [FILTER_KEYS.PRICING_BASIS]: countedLabelColumn(FILTER_KEYS.PRICING_BASIS),
  [FILTER_KEYS.UNITS]: countedLabelColumn(FILTER_KEYS.UNITS),
  [FILTER_KEYS.CURRENCY]: countedLabelColumn(FILTER_KEYS.CURRENCY),
  [FILTER_KEYS.FREQUENCY]: countedLabelColumn(FILTER_KEYS.FREQUENCY),
  [FILTER_KEYS.INDEX_MODULE]: countedLabelColumn(FILTER_KEYS.INDEX_MODULE, true)
};
export const oilOnlyModules = [
  'AfricaCrude',
  'AmericasMarine',
  'AsiaBiofuels',
  'AsiaCrude',
  'AsiaFeedstocks',
  'AsiaFuelOil',
  'AsiaGasoil',
  'AsiaGasoline',
  'AsiaJet',
  'AsiaLPG',
  'AsiaMarine',
  'AsiaNaphtha',
  'EuropeanBiofuels',
  'EuropeanCrude',
  'EuropeanFeedstocks',
  'EuropeanFuelOil',
  'EuropeanGasoil',
  'EuropeanGasoline',
  'EuropeanJet',
  'EuropeanLPG',
  'EuropeanMarine',
  'EuropeanNaphtha',
  'GlobalIndexes',
  'GlobalOilSelect',
  'MiddleEastCrude',
  'MiddleEastFuelOil',
  'MiddleEastGasoil',
  'MiddleEastGasoline',
  'MiddleEastJet',
  'MiddleEastNaphtha',
  'NorthAmericaBiofuels',
  'NorthAmericaCrude',
  'NorthAmericaDistillate',
  'NorthAmericaFeedstocks',
  'NorthAmericaFuelOil',
  'NorthAmericaGasoline',
  'NorthAmericaJet',
  'NorthAmericaNaphtha',
  'NorthAmericaNGLs',
  'NorthAmericaStorage',
  'SouthAmericaCrude'
];

export const energyTransitionModules = [
  'EuropeanSAF',
  'AsiaSAF',
  'NorthAmericaSAF',
  'AsiaBiofuels',
  'EuropeanBiofuels',
  'GlobalHydrogen',
  'GlobalMarineCarbon',
  'GlobalVoluntaryCarbon',
  'NorthAmericaBiofuels',
  'AsiaComplianceCarbon'
];

const symbolColumn = { accessorKey: 'Symbol', header: '', size: 35, enableGlobalFilter: false };
const factsheetColumn = {
  accessorKey: 'Factsheet',
  header: 'Info',
  size: 50,
  enableGlobalFilter: false
};
const statusColumn = {
  accessorKey: 'Status',
  header: '',
  size: 0,
  filterFn: filterByStatus,
  enableGlobalFilter: false
};
const launchDateColumn = {
  accessorKey: 'LaunchDate',
  header: '',
  size: 56,
  enableSorting: false,
  enableGlobalFilter: false,
  filterFn: filterByNewData
};
const timeRefColumn = {
  accessorKey: 'TimeRef',
  header: 'TimeRef',
  size: 95,
  sortingFn: sortAsNumber,
  enableGlobalFilter: false
};
const periodTypeColumn = {
  accessorKey: 'PeriodType',
  header: 'PeriodType',
  size: 120,
  sortingFn: sortAsPeriodType,
  enableGlobalFilter: false
};
const currencyColumn = {
  accessorKey: 'Currency',
  header: 'Currency',
  size: 100,
  enableGlobalFilter: false
};
const commodityColumn = {
  accessorKey: 'Commodity',
  header: 'Commodity',
  size: 130,
  enableGlobalFilter: false
};
const deliveryBasisColumn = {
  accessorKey: 'DeliveryBasis',
  header: 'Delivery Basis',
  size: 150,
  enableGlobalFilter: false
};
const pricingBasisColumn = {
  accessorKey: 'PricingBasis',
  header: 'Pricing Basis',
  size: 150,
  enableGlobalFilter: false
};
const indexModuleColumn = {
  accessorKey: 'IndexModule',
  header: 'Index Module',
  size: 200,
  filterFn: filterFns.arrIncludesSome,
  enableGlobalFilter: false
};
const factsheetVersionColumn = {
  accessorKey: 'FactsheetVersion',
  header: 'Latest Factsheet',
  size: 170,
  enableGlobalFilter: false
};
const timeZoneColumn = {
  accessorKey: 'TimeZone',
  header: 'Timezone',
  size: 140,
  enableGlobalFilter: false
};
const holidayCalendarColumn = {
  accessorKey: 'HolidayCalendar',
  header: 'Holiday Calendar',
  size: 220,
  enableGlobalFilter: false
};
const lastTradeDateCalendarColumn = {
  accessorKey: 'LastTradeDateCalendar',
  header: 'Last Trade Date Calendar',
  size: 220,
  enableGlobalFilter: false
};

export const spotColumns = [
  symbolColumn,
  factsheetColumn,
  statusColumn,
  launchDateColumn,
  { accessorKey: 'Title', header: 'Full Title', size: 650 },
  {
    accessorKey: 'Code',
    header: 'Code',
    size: 100,
    filterFn: filterBySelectedCode
  },
  timeRefColumn,
  periodTypeColumn,
  { accessorKey: 'Period', header: 'Period', size: 100, enableGlobalFilter: false },
  { accessorKey: 'Alias', header: 'Alias' },
  {
    accessorKey: ADDITIONAL_ITEM,
    header: ADDITIONAL_ITEM,
    size: 40,
    enableGlobalFilter: false
  },
  { accessorKey: 'LastDate', header: 'Last Date', size: 100, enableGlobalFilter: false },
  {
    accessorKey: 'Last',
    header: 'Last',
    size: 100,
    sortingFn: sortAsNumber,
    enableGlobalFilter: false
  },
  {
    accessorKey: 'Change',
    header: 'Change',
    size: 100,
    sortingFn: sortAsNumber,
    enableGlobalFilter: false
  },
  currencyColumn,
  { accessorKey: 'Units', header: 'Units', size: 80, enableGlobalFilter: false },
  commodityColumn,
  { accessorKey: 'TradingHub', header: 'Trading Hub', size: 120, enableGlobalFilter: false },
  deliveryBasisColumn,
  pricingBasisColumn,
  {
    accessorKey: 'Frequency',
    header: 'Frequency',
    size: 120,
    sortingFn: sortAsFrequency,
    enableGlobalFilter: false
  },
  indexModuleColumn,
  { accessorKey: 'PeriodAbs', header: 'Period Abs', size: 120, enableGlobalFilter: false },
  { accessorKey: 'StartDate', header: 'Start Date', size: 120, enableGlobalFilter: false },
  { accessorKey: 'EndDate', header: 'End Date', size: 120, enableGlobalFilter: false },
  factsheetVersionColumn,
  timeZoneColumn,
  holidayCalendarColumn,
  lastTradeDateCalendarColumn,
  {
    accessorKey: 'Increment',
    header: 'Increment',
    size: 110,
    sortingFn: sortAsNumber,
    enableGlobalFilter: false
  },
  { accessorKey: 'Index', header: 'Columns Available', size: 170, enableGlobalFilter: false }
];

export const forwardCurvesColumns = [
  symbolColumn,
  factsheetColumn,
  statusColumn,
  launchDateColumn,
  { accessorKey: 'Title', header: 'Full Title', size: 550 },
  { accessorKey: 'Code', header: 'Code', size: 100, filterFn: filterBySelectedCurve },
  timeRefColumn,
  periodTypeColumn,
  { accessorKey: 'Periods', header: 'NumPeriods', size: 120, enableGlobalFilter: false },
  currencyColumn,
  { accessorKey: 'Units', header: 'Units', size: 100, enableGlobalFilter: false },
  commodityColumn,
  { accessorKey: 'TradingHub', header: 'Trading Hub', enableGlobalFilter: false },
  deliveryBasisColumn,
  pricingBasisColumn,
  { accessorKey: 'Frequency', header: 'Frequency', size: 120, enableGlobalFilter: false },
  indexModuleColumn,
  { accessorKey: 'StartDate', header: 'StartDate', size: 120, enableGlobalFilter: false },
  { accessorKey: 'EndDate', header: 'EndDate', size: 120, enableGlobalFilter: false },
  factsheetVersionColumn,
  timeZoneColumn,
  holidayCalendarColumn,
  lastTradeDateCalendarColumn,
  { accessorKey: 'Increment', header: 'Increment', size: 110, enableGlobalFilter: false },
  {
    accessorKey: 'Index',
    header: 'Columns Available',
    size: 170,
    sortingFn: sortAsNumber,
    enableGlobalFilter: false
  },
  {
    accessorKey: 'PeriodMin',
    size: 0,
    enableGlobalFilter: false
  },
  {
    accessorKey: 'PeriodMax',
    size: 0,
    enableGlobalFilter: false
  }
];

export const defaultVisibilityState = {
  Status: false,
  TimeRef: false,
  PeriodType: false,
  Period: false,
  StartDate: false,
  EndDate: false,
  FactsheetVersion: false,
  TimeZone: false,
  HolidayCalendar: false,
  LastTradeDateCalendar: false,
  Increment: false,
  Index: false,
  PricingBasis: false,
  DeliveryBasis: false,
  Frequency: false,
  IndexModule: false,
  PeriodMax: false,
  PeriodMin: false,
  PeriodAbs: false
};

export const displayedValueSettingsColumnsList = [
  {
    label: 'Additional Values',
    columns: [
      {
        displayLabel: 'Period Abs',
        accessorKey: 'PeriodAbs'
      }
    ]
  }
];

export const displayedMetadataSettingsColumnsList = [
  {
    label: 'Default columns',
    columns: [
      {
        displayLabel: 'Currency',
        accessorKey: 'Currency'
      },
      {
        displayLabel: 'Units',
        accessorKey: 'Units'
      },
      {
        displayLabel: 'Commodity',
        accessorKey: 'Commodity'
      },
      {
        displayLabel: 'Trading Hub',
        accessorKey: 'TradingHub'
      }
    ]
  },
  {
    label: 'Additional Filters',
    columns: [
      {
        displayLabel: 'Delivery Basis',
        accessorKey: 'DeliveryBasis'
      },
      {
        displayLabel: 'Pricing Basis',
        accessorKey: 'PricingBasis'
      },
      {
        displayLabel: 'Frequency',
        accessorKey: 'Frequency'
      },
      {
        displayLabel: 'Index Module',
        accessorKey: 'IndexModule'
      }
    ]
  },
  {
    label: 'Additional columns',
    columns: [
      {
        displayLabel: 'Start Date',
        accessorKey: 'StartDate'
      },
      {
        displayLabel: 'End Date',
        accessorKey: 'EndDate'
      },
      {
        displayLabel: 'Latest Factsheet',
        accessorKey: 'FactsheetVersion'
      },
      {
        displayLabel: 'Timezone',
        accessorKey: 'TimeZone'
      },
      {
        displayLabel: 'Holiday Calendar',
        accessorKey: 'HolidayCalendar'
      },
      {
        displayLabel: 'Last Trade Date Calendar',
        accessorKey: 'LastTradeDateCalendar'
      },
      {
        displayLabel: 'Increment',
        accessorKey: 'Increment'
      },
      {
        displayLabel: 'Columns Available',
        accessorKey: 'Index'
      }
    ]
  }
];

export const alphabeticallySortedFilters = ['IndexModule'];

export const allColumnIdsToChangeVisibility = [
  'Units',
  'Currency',
  'Commodity',
  'TradingHub',
  'DeliveryBasis',
  'PricingBasis',
  'FactsheetVersion',
  'Frequency',
  'StartDate',
  'EndDate',
  'IndexModule',
  'TimeZone',
  'HolidayCalendar',
  'LastTradeDateCalendar',
  'Increment',
  'Index'
];
